import { useState, useEffect } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.includes('/uploads/')) {
    const uploadsPath = url.substring(url.indexOf('/uploads'));
    return `${BACKEND_URL}/api${uploadsPath}`;
  }
  return url;
};

export default function AdminParents() {
  const [parents, setParents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingParent, setEditingParent] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const emptyParent = {
    name: '',
    gender: 'Female',
    breed: 'Scottish Straight',
    color: '',
    images: [],
    description: '',
  };

  const [parentForm, setParentForm] = useState(emptyParent);

  useEffect(() => {
    fetchParents();
  }, []);

  const fetchParents = async () => {
    try {
      const response = await adminApi.get(`/parents`);
      setParents(response.data);
    } catch (error) {
      toast.error('Failed to load parents');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedData = {
      ...parentForm,
      images: parentForm.images.filter((img) => img.trim() !== ''),
    };

    try {
      if (editingParent) {
        await adminApi.put(`/parents/${editingParent.id}`, formattedData);
        toast.success('Parent updated successfully!');
      } else {
        await adminApi.post(`/parents`, formattedData);
        toast.success('Parent added successfully!');
      }
      fetchParents();
      setIsDialogOpen(false);
      setParentForm(emptyParent);
      setEditingParent(null);
    } catch {
      // Silently fail
      toast.error('Failed to save parent');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this parent?')) return;

    try {
      await adminApi.delete(`/parents/${id}`);
      toast.success('Parent deleted successfully!');
      fetchParents();
    } catch {
      // Silently fail
      toast.error('Failed to delete parent');
    }
  };

  const handleEdit = (parent) => {
    setEditingParent(parent);
    setParentForm({
      ...parent,
      images: parent.images || [],
    });
    setIsDialogOpen(true);
  };

  // Handle file upload - Using Cloudinary for permanent URLs
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        toast.info(`Uploading ${file.name}...`);
        
        // Upload to Cloudinary
        const result = await uploadToCloudinary(file, 'parents');
        uploadedUrls.push(result.url);
      }

      setParentForm({
        ...parentForm,
        images: [...parentForm.images, ...uploadedUrls],
      });

      toast.success(`${uploadedUrls.length} image(s) uploaded successfully!`);
    } catch {
      // Silently fail
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(Array.from(e.target.files));
    }
  };

  const removeImageField = (index) => {
    const newImages = parentForm.images.filter((_, i) => i !== index);
    setParentForm({ ...parentForm, images: newImages });
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Heart className="w-6 h-6 text-[#E85D75]" />
          <h2 className="text-2xl font-serif font-medium text-stone-800">Breeding Parents</h2>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-parent-button"
              onClick={() => {
                setEditingParent(null);
                setParentForm(emptyParent);
              }}
              className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Parent
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingParent ? 'Edit Parent' : 'Add New Parent'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Name *
                  </label>
                  <Input
                    value={parentForm.name}
                    onChange={(e) => setParentForm({ ...parentForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Gender *</label>
                  <Select
                    value={parentForm.gender}
                    onValueChange={(value) => setParentForm({ ...parentForm, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Breed *</label>
                  <Select
                    value={parentForm.breed}
                    onValueChange={(value) => setParentForm({ ...parentForm, breed: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Scottish Fold">Scottish Fold</SelectItem>
                      <SelectItem value="Scottish Straight">Scottish Straight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Color *</label>
                  <Input
                    value={parentForm.color}
                    onChange={(e) => setParentForm({ ...parentForm, color: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Upload Images
                </label>

                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-[#E85D75] bg-[#E85D75]/5'
                      : 'border-stone-300 hover:border-[#E85D75]/50'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="text-4xl">📤</div>
                    <div>
                      <p className="text-sm font-medium text-stone-700">Drag & drop images here</p>
                      <p className="text-xs text-stone-500 mt-1">or click to browse</p>
                    </div>
                    <input
                      type="file"
                      id="parent-file-upload"
                      multiple
                      accept="image/*"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('parent-file-upload').click()}
                      variant="outline"
                      disabled={uploadingImages}
                      className="mt-2"
                    >
                      {uploadingImages ? 'Uploading...' : '📁 Browse Files'}
                    </Button>
                    <p className="text-xs text-stone-400">JPG, PNG, WEBP (Max 5MB each)</p>
                  </div>
                </div>

                {parentForm.images.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-stone-600 mb-3">
                      Uploaded Images ({parentForm.images.length})
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {parentForm.images.map((image, index) => (
                        <div
                          key={`parent-image-preview-${index}-${image.slice(-10)}`}
                          className="relative group aspect-square rounded-lg overflow-hidden border border-stone-200"
                        >
                          <img
                            src={normalizeImageUrl(image)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeImageField(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Description
                </label>
                <Textarea
                  value={parentForm.description}
                  onChange={(e) => setParentForm({ ...parentForm, description: e.target.value })}
                  rows={4}
                  placeholder="Tell us about this parent cat..."
                />
              </div>

              <Button
                type="submit"
                data-testid="save-parent-button"
                className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
              >
                {editingParent ? 'Update Parent' : 'Add Parent'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {parents.map((parent) => (
          <div
            key={parent.id}
            data-testid={`parent-card-${parent.id}`}
            className="bg-stone-50 rounded-2xl overflow-hidden border border-stone-100"
          >
            {parent.images && parent.images.length > 0 ? (
              <div className="aspect-square overflow-hidden">
                <img
                  src={normalizeImageUrl(parent.images[0])}
                  alt={parent.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-square bg-stone-200 flex items-center justify-center">
                <Heart className="w-12 h-12 text-stone-400" />
              </div>
            )}
            <div className="p-4">
              <h3 className="text-xl font-serif font-medium text-stone-800 mb-1">
                {parent.name}
              </h3>
              <p className="text-sm text-stone-600 mb-2">
                {parent.breed} • {parent.gender} • {parent.color}
              </p>
              {parent.description && (
                <p className="text-sm text-stone-600 mb-3 line-clamp-2">{parent.description}</p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleEdit(parent)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(parent.id)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {parents.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          <Heart className="w-16 h-16 mx-auto mb-4 text-stone-200" />
          <p>No parent cats added yet. Click "Add Parent" to get started.</p>
        </div>
      )}
    </div>
  );
}
